import { forwardRef, useEffect, useState } from "react";
import paginationFactory from "react-bootstrap-table2-paginator";
import "react-bootstrap-table-next/dist/react-bootstrap-table2.min.css";
import "react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.min.css";
import BootstrapTable from "react-bootstrap-table-next";
import "./DataGrid.css";

//App
import PlusIcon from "../../Icons/PlusIcon";
import DashIcon from "../../Icons/DashIcon";
import { DataGridProps } from "../../../../types";
import useGridStyles from "./styles";

const DataGrid = forwardRef<BootstrapTable<any>, DataGridProps>(
  (
    {
      columns,
      data = [],
      keyField = "id",
      noDataMessage = "No Rows",
      totalSize = 0,
      isExpandableRow,
      isRowSelectable,
      onSelectionChange,
      expandRowData,
      rowEvents,
      remote = false,
      page = 1,
      pageSize,
      handlePageChange,
      defaultSorted,
    },
    ref
  ) => {
    const customPaginationTotalRender = (
      from: number,
      to: number,
      size: number
    ) => (
      <span className="react-bootstrap-table-pagination-total paginationText">
        {from} - {to} of {size}
      </span>
    );
    const [gridHeight, setGridHeight] = useState(window.innerHeight);
    const [selectedRows, setSelectedRows] = useState<any[]>([]);

    useEffect(() => {
      // Call the callback whenever selected rows change
      if (onSelectionChange) onSelectionChange(selectedRows);
    }, [selectedRows]);

    useEffect(() => {
      const handleResize = () => {
        setGridHeight(window.innerHeight);
      };

      window.addEventListener("resize", handleResize);

      return () => window.removeEventListener("resize", handleResize);
    }, []);

    const handleOnSelect = (row: any, isSelected: boolean) => {
      const selected = isSelected
        ? [...selectedRows, row]
        : selectedRows.filter((selectedRow) => selectedRow?.id !== row?.id);
      setSelectedRows(selected);
    };

    const handleOnSelectAll = (isSelected: boolean, rows: any) => {
      const selected = isSelected
        ? [...selectedRows, ...rows]
        : selectedRows.filter(
            (selectedRow) => !rows.some((row: any) => row.id === selectedRow.id)
          );
      setSelectedRows(selected);
    };

    const selectRow = isRowSelectable
      ? {
          mode: "checkbox" as const,
          clickToSelect: false,
          clickToExpand: false,
          onSelect: handleOnSelect,
          onSelectAll: handleOnSelectAll,
          selected: selectedRows.map((row) => row.id),
        }
      : undefined;

    const expandRow = isExpandableRow
      ? {
          showExpandColumn: true,
          expandByColumnOnly: true,
          onlyOneExpanding: true,
          renderer: (row: any) => expandRowData(row),
          expandHeaderColumnRenderer: ({
            isAnyExpands,
          }: {
            isAnyExpands: boolean;
          }) => {
            if (isAnyExpands) {
              return <DashIcon />;
            }
            return <PlusIcon />;
          },
          expandColumnRenderer: ({ expanded }: { expanded: boolean }) => {
            if (expanded) {
              return <DashIcon />;
            }
            return <PlusIcon />;
          },
        }
      : undefined;

    const options = {
      custom: false,
      page,
      paginationSize: 5,
      sizePerPage: pageSize,
      sizePerPageList: [
        {
          text: "10",
          value: 10,
        },
        {
          text: "25",
          value: 25,
        },
        {
          text: "50",
          value: 50,
        },
        {
          text: "100",
          value: 100,
        },
      ],

      pageStartIndex: 1,
      firstPageText: "|<",
      prePageText: "<",
      nextPageText: ">",
      lastPageText: ">|",
      nextPageTitle: "Next page",
      prePageTitle: "Pre page",
      firstPageTitle: "First page",
      lastPageTitle: "Last page",
      withFirstAndLast: true,
      totalSize: totalSize,
      alwaysShowAllBtns: false,
      showTotal: false,
      hideSizePerPage: false,
      hidePageListOnlyOnePage: false,
      paginationTotalRenderer: customPaginationTotalRender,
    };

    const paginationProps = paginationFactory(options);
    const gridStyles = useGridStyles(gridHeight);

    return (
      <div className="table-container" style={gridStyles.gridContainer}>
        <BootstrapTable
          defaultSorted={defaultSorted}
          remote={remote}
          //bootstrap4
          keyField={keyField}
          data={data}
          columns={columns}
          pagination={paginationProps}
          selectRow={selectRow}
          expandRow={expandRow}
          noDataIndication={() => <div>{noDataMessage}</div>}
          //hover
          condensed
          rowEvents={rowEvents}
          onTableChange={(
            type,
            { page, sizePerPage, sortField, sortOrder }
          ) => {
            if (handlePageChange) {
              handlePageChange(page, sizePerPage, sortField, sortOrder);
            }
          }}
          ref={ref}
          wrapperClasses="table-responsive border rounded mb-3"
          headerWrapperClasses="header-datagrid"
          classes="table-datagrid"
        />
      </div>
    );
  }
);

export default DataGrid;
